import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Eye, EyeOff, Loader2, ArrowLeft, KeyRound, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { ForcePasswordChangeForm } from '../../components/admin/ForcePasswordChangeForm';

export default function AdminLoginPage() {
    const navigate = useNavigate();
    const { login, isAuthenticated, complete2FASetup, fetchSetup2FA } = useAdminAuth();
    const [needsPwChange, setNeedsPwChange] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [needs2FA, setNeeds2FA] = useState(false);
    const [needs2FASetup, setNeeds2FASetup] = useState(false);
    const [setupData, setSetupData] = useState(null);
    const [totpCode, setTotpCode] = useState('');

    if (isAuthenticated) {
        navigate('/admin/dashboard', { replace: true });
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (needs2FASetup) {
                const r = await complete2FASetup(totpCode);
                if (r?.requires_password_change) {
                    setNeeds2FASetup(false);
                    setNeedsPwChange(true);
                    toast.info('2FA activado. Ahora define una contraseña nueva.');
                    return;
                }
                toast.success('2FA activado. Bienvenido.');
                navigate('/admin/dashboard');
                return;
            }
            const result = await login(email, password, needs2FA ? totpCode : null);
            if (result.requires_2fa_setup) {
                // Auditoria 8 - Item 1: primer login SUPERADMIN sin 2FA. Forzar setup.
                setNeeds2FASetup(true);
                const data = await fetchSetup2FA();
                setSetupData(data);
                toast.info('2FA obligatorio. Escanea el QR y confirma con un codigo.');
            } else if (result.requires_password_change) {
                setNeeds2FA(false);
                setNeedsPwChange(true);
                toast.info('Debes cambiar la contraseña inicial antes de continuar.');
            } else if (result.requires_2fa) {
                setNeeds2FA(true);
                toast.info('Ingresa tu codigo de autenticacion');
            } else {
                toast.success('Acceso concedido');
                navigate('/admin/dashboard');
            }
        } catch (error) {
            const msg = error.response?.data?.detail || error.message || 'Credenciales invalidas';
            toast.error(typeof msg === 'string' ? msg : msg.message || 'Error');
            if (needs2FA || needs2FASetup) setTotpCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setNeeds2FA(false);
        setNeeds2FASetup(false);
        setSetupData(null);
        setTotpCode('');
        setPassword('');
        localStorage.removeItem('aeroops_admin_setup_token');
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <div className="absolute inset-0 aviation-gradient opacity-90" />
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(245,158,11,0.08) 0%, transparent 60%)' }} />

            <a href="/" className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="admin-back-to-landing">
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio
            </a>

            <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur-xl border-border/50 animate-fade-in">
                <CardHeader className="text-center pb-2">
                    <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 border transition-colors duration-300 ${needs2FA || needs2FASetup ? 'bg-primary/10 border-primary/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
                        {needs2FASetup ? <QrCode className="w-8 h-8 text-primary" /> : (needs2FA ? <KeyRound className="w-8 h-8 text-primary" /> : <Shield className="w-8 h-8 text-amber-500" />)}
                    </div>
                    <CardTitle className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        {needsPwChange ? 'Nueva contraseña' : needs2FASetup ? 'Activar 2FA' : (needs2FA ? 'Verificacion 2FA' : 'AeroOps Admin')}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                        {needsPwChange
                            ? 'La contraseña inicial debe cambiarse en el primer inicio de sesión.'
                            : needs2FASetup
                            ? '2FA obligatorio para Super Admin. Escanea el QR con Google Authenticator y confirma con un codigo.'
                            : (needs2FA ? 'Ingresa el codigo de tu aplicacion de autenticacion' : 'Panel de Administracion de Plataforma')}
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    {needsPwChange && (
                        <ForcePasswordChangeForm currentPassword={password} onDone={() => navigate('/admin/dashboard')} />
                    )}
                    {!needsPwChange && needs2FASetup && setupData && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="p-4 rounded-lg border border-border bg-muted/20 space-y-3">
                                <div className="flex items-center justify-center bg-white p-4 rounded">
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(setupData.uri)}&size=200x200`}
                                        alt="QR 2FA"
                                        className="w-40 h-40"
                                        data-testid="admin-2fa-qr"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground text-center">O ingresa este secreto manualmente</p>
                                    <p className="text-center font-mono text-xs bg-background p-2 rounded border border-border select-all" data-testid="admin-2fa-secret">{setupData.secret}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="totp-setup-code">Codigo generado</Label>
                                <Input
                                    id="totp-setup-code"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    required
                                    autoFocus
                                    data-testid="admin-2fa-setup-input"
                                    className="input-aviation text-center text-2xl font-mono tracking-[0.5em] py-3"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider text-xs px-6 py-2.5"
                                disabled={loading || totpCode.length < 6}
                                data-testid="admin-2fa-setup-submit"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                Activar 2FA y continuar
                            </Button>
                            <Button type="button" variant="ghost" className="w-full text-xs text-muted-foreground" onClick={handleBack} data-testid="admin-2fa-setup-back">
                                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Cancelar y volver al login
                            </Button>
                        </form>
                    )}
                    {!needsPwChange && !needs2FA && !needs2FASetup && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="admin-email">Correo Electronico</Label>
                                <Input
                                    id="admin-email"
                                    type="email"
                                    placeholder="admin@aeroops.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="admin-login-email"
                                    className="input-aviation"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin-password">Contrasena</Label>
                                <div className="relative">
                                    <Input
                                        id="admin-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="********"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="admin-login-password"
                                        className="input-aviation pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs px-6 py-2.5"
                                disabled={loading}
                                data-testid="admin-login-submit"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                Acceder al Panel
                            </Button>
                            <div className="text-center pt-2">
                                <a href="/admin/forgot-password" className="text-xs text-amber-500 hover:underline" data-testid="admin-forgot-password-link">
                                    ¿Olvidaste tu contraseña?
                                </a>
                            </div>
                        </form>
                    )}
                    {!needsPwChange && needs2FA && !needs2FASetup && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="totp-code">Codigo de 6 digitos</Label>
                                <Input
                                    id="totp-code"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    required
                                    autoFocus
                                    data-testid="admin-totp-input"
                                    className="input-aviation text-center text-2xl font-mono tracking-[0.5em] py-3"
                                />
                                <p className="text-xs text-muted-foreground text-center">
                                    Abre Google Authenticator o tu app TOTP
                                </p>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider text-xs px-6 py-2.5"
                                disabled={loading || totpCode.length < 6}
                                data-testid="admin-totp-submit"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                Verificar Codigo
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                className="w-full text-xs text-muted-foreground"
                                onClick={handleBack}
                                data-testid="admin-totp-back"
                            >
                                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Volver al login
                            </Button>
                        </form>
                    )}

                    <div className="mt-6 text-center">
                        <a href="/login" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                            Acceso para aerolineas
                        </a>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
